<template>
  <div
    class="payer-score-report-preview"
    :class="{
      'is-exporting': downloadingPdf,
      'is-embedded': embedded,
      'is-single-page-view': singlePageView
    }"
  >
    <div class="preview-header">
      <h3 class="preview-section-title">Report Preview</h3>
      <div class="preview-actions">
        <button
          type="button"
          class="preview-action-btn preview-action-btn-primary"
          :disabled="downloadingPdf"
          @click="downloadPdf"
        >
          <i v-if="downloadingPdf" class="las la-spinner la-spin"></i>
          <i v-else class="las la-file-pdf"></i>
          {{ downloadingPdf ? 'Generating PDF...' : 'Download PDF' }}
        </button>
      </div>
    </div>

    <div ref="pagesContainer" class="pdf-export-content">
      <!-- Page 1: Cover -->
      <article class="report-page report-page-cover" data-report-page="1" v-show="pageVisible(1)">
        <header class="report-page-mini-header">
          <span>{{ reportTitle }}</span>
        </header>

        <div class="cover-body">
          <h1 class="cover-title">
            Kema Payer Assessment Report
          </h1>
          <p class="cover-subtitle">
            Merchant Name –
            <span class="cover-merchant">{{ data.merchantName }}</span>
          </p>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 1/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 2: Executive Summary -->
      <article class="report-page" data-report-page="2" v-show="pageVisible(2)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner">
          {{ data.merchantName }} PAYER ASSESSMENT REPORT
        </div>

        <table class="report-info-table">
          <tbody>
            <tr>
              <td class="label-cell">Prepared By:</td>
              <td>{{ displayValue(data.preparedBy) }}</td>
              <td class="label-cell">Date:</td>
              <td>{{ displayValue(data.reportDate) }}</td>
            </tr>
            <tr>
              <td class="label-cell">Merchant Name:</td>
              <td>{{ displayValue(data.merchantName) }}</td>
              <td class="label-cell">Establishment Date:</td>
              <td>{{ displayValue(data.establishmentDate) }}</td>
            </tr>
            <tr>
              <td class="label-cell">Industry:</td>
              <td>{{ displayValue(data.industry) }}</td>
              <td class="label-cell">Key Shareholder(s)/ Authorized Signatory:</td>
              <td>{{ displayValue(data.keyShareholder) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="report-section-banner">EXECUTIVE SUMMARY</div>

        <div class="report-summary-panel report-requirement-panel">
          <h4 class="report-subheading">{{ data.merchantName }} Requirement</h4>
          <p class="report-paragraph" v-html="formatRichText(data.companyOverview)"></p>

          <table class="report-info-table report-summary-panel-table report-requirement-table">
            <tbody>
              <tr>
                <td class="label-cell">Facility Requested</td>
                <td>{{ displayValue(data.facilityRequested) }}</td>
              </tr>
              <tr>
                <td class="label-cell">Facility Limit Requested</td>
                <td>{{ displayValue(data.facilityLimit) }}</td>
              </tr>
              <tr>
                <td class="label-cell">Tenor Requested</td>
                <td>{{ displayValue(data.tenorRequested) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 2/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 3: Payer Analysis -->
      <article class="report-page report-page-payer-analysis" data-report-page="3" v-show="pageVisible(3)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          {{ data.merchantName }} PAYER ANALYSIS
        </div>

        <section class="payer-metrics-section">
          <div class="payer-metrics-text-box">
            <h4 class="report-subheading payer-metrics-overview-heading">
              {{ data.merchantName }} Prime Payers Overview
            </h4>

            <ul class="conclusion-list payer-metrics-overview-list">
              <li
                v-for="(item, index) in data.primePayersOverview"
                :key="`prime-payers-overview-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.primePayersOverview.length" class="conclusion-empty">—</li>
            </ul>
          </div>

          <table class="report-info-table payer-metrics-table">
            <thead>
              <tr>
                <th>Key Assessment Metrics - Prime Payers</th>
                <th>Recent Value (last 12 months)</th>
                <th>Lifetime Value (last 8 years)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in primePayerMetricRows"
                :key="`prime-payer-metric-${index}`"
              >
                <td class="label-cell">{{ row.label }}</td>
                <td>{{ row.recent }}</td>
                <td>{{ row.lifetime }}</td>
              </tr>
            </tbody>
          </table>

          <p
            v-if="data.primePayerPcrFootnote"
            class="payer-metrics-footnote"
            v-html="formatRichText(data.primePayerPcrFootnote)"
          ></p>
        </section>

        <section class="payer-metrics-section payer-metrics-section-spaced">
          <table class="report-info-table payer-metrics-table industry-metrics-table">
            <thead>
              <tr>
                <th>Customers by Industry Type</th>
                <th>AR Outstanding (AED)</th>
                <th>Sales Contribution (AED)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in data.primePayerSectorStats"
                :key="`industry-row-${index}`"
              >
                <td>{{ displayValue(row.industryType) }}</td>
                <td>{{ formatTableAmount(row.arOutstanding) }}</td>
                <td>{{ formatTableAmount(row.salesContribution) }}</td>
              </tr>
              <tr v-if="!data.primePayerSectorStats.length">
                <td colspan="3">—</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="report-footnotes">
          <p>¹ Refer to Appendix A for details on selection criteria for prime payers</p>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 3/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 4: Prime Payer Statistics -->
      <article class="report-page report-page-prime-payer-stats" data-report-page="4" v-show="pageVisible(4)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table prime-payer-outstanding-table">
          <thead>
            <tr>
              <th>Prime Payer Names</th>
              <th>Industry Sector</th>
              <th>AR Outstanding (AED)</th>
              <th>% of Total AR</th>
              <th>Weighted ADTP (days - from due date - last 12 months)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.primePayersOutstandingStats"
              :key="`prime-payer-outstanding-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td>{{ displayValue(row.payer_sector) }}</td>
              <td class="numeric-cell">{{ formatTableAmount(row.oustanding_aed) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.oustanding_pct) }}</td>
              <td class="numeric-cell">{{ formatAdtpValue(row.weighted_adtp) }}</td>
            </tr>
            <tr v-if="!data.primePayersOutstandingStats.length">
              <td colspan="5">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 4/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 5: Prime Payer Sales -->
      <article class="report-page report-page-prime-payer-sales" data-report-page="5" v-show="pageVisible(5)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table prime-payer-sales-table">
          <thead>
            <tr>
              <th>Prime Payer Names</th>
              <th>Industry Sector</th>
              <th>Sales Contribution (AED)</th>
              <th>% Sales Contribution (AED)</th>
              <th>Associated Parent</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.primePayersSalesStats"
              :key="`prime-payer-sales-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td>{{ displayValue(row.payer_sector) }}</td>
              <td class="numeric-cell">{{ formatTableAmount(row.sales_aed) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.sales_pct) }}</td>
              <td>{{ displayValue(row.parent) }}</td>
            </tr>
            <tr v-if="!data.primePayersSalesStats.length">
              <td colspan="5">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 5/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 6: Prime Payer Aging -->
      <article class="report-page report-page-prime-payer-aging" data-report-page="6" v-show="pageVisible(6)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table prime-payer-aging-table">
          <colgroup>
            <col
              v-for="column in primePayerAgingTableColumns"
              :key="`aging-col-${column.key}`"
              :style="{ width: column.width }"
            />
          </colgroup>
          <thead>
            <tr>
              <th rowspan="2" class="payer-name-header">{{ primePayerAgingTableColumns[0].label }}</th>
              <th colspan="5">Aging Analysis - Invoice Amounts (last 12 months)</th>
            </tr>
            <tr>
              <th
                v-for="column in primePayerAgingPercentColumns"
                :key="`aging-head-${column.key}`"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.primePayersAgingStats"
              :key="`prime-payer-aging-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['0_30_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['31_60_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['61_90_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['>90_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.pcr) }}</td>
            </tr>
            <tr v-if="!data.primePayersAgingStats.length">
              <td colspan="6">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 6/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 7: Approved Payer Analysis -->
      <article class="report-page report-page-payer-analysis" data-report-page="7" v-show="pageVisible(7)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <section class="payer-metrics-section">
          <div class="payer-metrics-text-box">
            <h4 class="report-subheading payer-metrics-overview-heading">
              {{ data.merchantName }} Approved Payers Overview
            </h4>

            <ul class="conclusion-list payer-metrics-overview-list">
              <li
                v-for="(item, index) in data.approvedPayersOverview"
                :key="`approved-payers-overview-${index}`"
                v-html="formatRichText(item)"
              ></li>
              <li v-if="!data.approvedPayersOverview.length" class="conclusion-empty">—</li>
            </ul>
          </div>

          <table class="report-info-table payer-metrics-table">
            <thead>
              <tr>
                <th>Key Assessment Metrics - Approved Payers</th>
                <th>Recent Value (last 12 months)</th>
                <th>Lifetime Value (last 8 years)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in approvedPayerMetricRows"
                :key="`approved-payer-metric-${index}`"
              >
                <td class="label-cell">{{ row.label }}</td>
                <td>{{ row.recent }}</td>
                <td>{{ row.lifetime }}</td>
              </tr>
            </tbody>
          </table>

          <p
            v-if="data.approvedPayerPcrFootnote"
            class="payer-metrics-footnote"
            v-html="formatRichText(data.approvedPayerPcrFootnote)"
          ></p>
        </section>

        <section class="payer-metrics-section payer-metrics-section-spaced">
          <table class="report-info-table payer-metrics-table industry-metrics-table">
            <thead>
              <tr>
                <th>Customers by Industry Type</th>
                <th>AR Outstanding (AED)</th>
                <th>Sales Contribution (AED)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in data.approvedPayerSectorStats"
                :key="`approved-industry-row-${index}`"
              >
                <td>{{ displayValue(row.industryType) }}</td>
                <td>{{ formatTableAmount(row.arOutstanding) }}</td>
                <td>{{ formatTableAmount(row.salesContribution) }}</td>
              </tr>
              <tr v-if="!data.approvedPayerSectorStats.length">
                <td colspan="3">—</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="report-footnotes">
          <p>¹ Refer to Appendix A for details on selection criteria for approved payers</p>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 7/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 8: Approved Payer Statistics -->
      <article class="report-page report-page-approved-payer-stats" data-report-page="8" v-show="pageVisible(8)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table approved-payer-outstanding-table">
          <colgroup>
            <col
              v-for="column in approvedPayerOutstandingTableColumns"
              :key="`approved-outstanding-col-${column.key}`"
              :style="{ width: column.width }"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="column in approvedPayerOutstandingTableColumns"
                :key="`approved-outstanding-head-${column.key}`"
                :class="{
                  'payer-name-header': column.key === 'payer_name',
                  'numeric-header': column.numeric
                }"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.approvedPayersOutstandingStats"
              :key="`approved-payer-outstanding-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td class="sector-cell">{{ displayValue(row.payer_sector) }}</td>
              <td class="numeric-cell">{{ formatTableAmount(row.oustanding_aed) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.oustanding_pct) }}</td>
              <td class="numeric-cell">{{ formatAdtpValue(row.weighted_adtp) }}</td>
            </tr>
            <tr v-if="!data.approvedPayersOutstandingStats.length">
              <td colspan="5">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 8/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 9: Approved Payer Sales -->
      <article class="report-page report-page-approved-payer-sales" data-report-page="9" v-show="pageVisible(9)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table approved-payer-sales-table">
          <colgroup>
            <col
              v-for="column in approvedPayerSalesTableColumns"
              :key="`approved-sales-col-${column.key}`"
              :style="{ width: column.width }"
            />
          </colgroup>
          <thead>
            <tr>
              <th
                v-for="column in approvedPayerSalesTableColumns"
                :key="`approved-sales-head-${column.key}`"
                :class="{
                  'payer-name-header': column.key === 'payer_name',
                  'numeric-header': column.numeric
                }"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.approvedPayersSalesStats"
              :key="`approved-payer-sales-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td class="sector-cell">{{ displayValue(row.payer_sector) }}</td>
              <td class="numeric-cell">{{ formatTableAmount(row.sales_aed) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.sales_pct) }}</td>
              <td class="parent-cell">{{ displayValue(row.parent) }}</td>
            </tr>
            <tr v-if="!data.approvedPayersSalesStats.length">
              <td colspan="5">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 9/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 10: Approved Payer Aging -->
      <article class="report-page report-page-approved-payer-aging" data-report-page="10" v-show="pageVisible(10)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <table class="report-info-table approved-payer-aging-table">
          <colgroup>
            <col
              v-for="column in approvedPayerAgingTableColumns"
              :key="`approved-aging-col-${column.key}`"
              :style="{ width: column.width }"
            />
          </colgroup>
          <thead>
            <tr>
              <th rowspan="2" class="payer-name-header">{{ approvedPayerAgingTableColumns[0].label }}</th>
              <th colspan="5">Aging Analysis - Invoice Amounts (last 12 months)</th>
            </tr>
            <tr>
              <th
                v-for="column in approvedPayerAgingPercentColumns"
                :key="`approved-aging-head-${column.key}`"
              >
                {{ column.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in data.approvedPayersAgingStats"
              :key="`approved-payer-aging-${index}`"
              :class="{ 'row-alt': index % 2 === 1 }"
            >
              <td>{{ displayValue(row.payer_name) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['0_30_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['31_60_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['61_90_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row['>90_days']) }}</td>
              <td class="numeric-cell">{{ formatPercentValue(row.pcr) }}</td>
            </tr>
            <tr v-if="!data.approvedPayersAgingStats.length">
              <td colspan="6">—</td>
            </tr>
          </tbody>
        </table>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 10/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 11: Conclusion & Recommendations -->
      <article class="report-page report-page-conclusion" data-report-page="11" v-show="pageVisible(11)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          CONCLUSION &amp; RECOMMENDATIONS
        </div>

        <div class="payer-conclusion-panel">
          <h4 class="payer-conclusion-heading">Conclusion</h4>
          <ul class="conclusion-list payer-conclusion-list">
            <li
              v-for="(item, index) in data.conclusionPoints"
              :key="`payer-conclusion-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.conclusionPoints.length" class="conclusion-empty">—</li>
          </ul>

          <h4 class="payer-conclusion-heading payer-recommendations-heading">
            Recommendations for Eligible Payers selection:
          </h4>
          <ul class="conclusion-list payer-conclusion-list">
            <li
              v-for="(item, index) in data.recommendationPoints"
              :key="`payer-recommendation-${index}`"
              v-html="formatRichText(item)"
            ></li>
            <li v-if="!data.recommendationPoints.length" class="conclusion-empty">—</li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 11/{{ totalPages }}</span>
        </footer>
      </article>

      <!-- Page 12: Appendix — Prime Payer Criteria -->
      <article class="report-page report-page-appendix" data-report-page="12" v-show="pageVisible(12)">
        <header class="report-page-header">
          <span class="report-page-header-label">{{ reportTitle }}</span>
          <img
            src="https://kema-assets.s3.amazonaws.com/media/images/kema-logo-on-light-0.6x.png"
            alt="Kema Logo"
            class="logo-img"
            crossorigin="anonymous"
          />
        </header>

        <div class="report-banner report-banner-left">
          APPENDIX
        </div>

        <div class="payer-appendix-panel">
          <h4 class="payer-appendix-heading">Prime Payer criteria</h4>
          <ul class="appendix-list">
            <li
              v-for="(item, index) in primePayerCriteria"
              :key="`prime-payer-criterion-${index}`"
              v-html="formatRichText(item)"
            ></li>
          </ul>
        </div>

        <footer class="report-page-footer">
          <span>{{ reportTitle }}</span>
          <span>Page 12/{{ totalPages }}</span>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  PAYER_REPORT_PREVIEW_PAGE_COUNT,
  PAYER_REPORT_TITLE
} from '../constants/payerReportEditorPages.js';
import {
  PRIME_PAYER_AGING_TABLE_COLUMNS,
  APPROVED_PAYER_OUTSTANDING_TABLE_COLUMNS,
  APPROVED_PAYER_SALES_TABLE_COLUMNS,
  APPROVED_PAYER_AGING_TABLE_COLUMNS
} from '../constants/payerReportTableConfigs.js';

const PAGE_WIDTH_PX = 816;
const PAGE_HEIGHT_PX = 1056;
const PDF_WIDTH_IN = 8.5;
const PDF_HEIGHT_IN = 11;

const PRIME_PAYER_CRITERIA = [
  'Corporate structure (preferably listed or part of a large group)',
  'Revenue > USD 300M',
  'Operating >10 years',
  'Employees strength >700',
  'Operations in UAE',
  'Presence in multiple countries',
  'No widely reported regulatory / financial controversies'
];

export default {
  name: 'PayerScoreReportPreview',
  props: {
    data: {
      type: Object,
      required: true
    },
    embedded: {
      type: Boolean,
      default: false
    },
    singlePageView: {
      type: Boolean,
      default: false
    },
    visiblePage: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      downloadingPdf: false,
      reportTitle: PAYER_REPORT_TITLE,
      totalPages: PAYER_REPORT_PREVIEW_PAGE_COUNT,
      primePayerCriteria: PRIME_PAYER_CRITERIA
    };
  },
  computed: {
    pdfFilename() {
      const name = (this.data.merchantName || 'payer_assessment')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '_');
      return `${name}_Payer_Assessment_Report.pdf`;
    },
    primePayerMetricRows() {
      return this.buildPayerMetricRows(this.data.primePayerStats, {
        adtpLabel: 'Weighted ADTP (from due date)',
        pcrRecentAsterisk: Boolean(this.data.primePayerPcrFootnote),
        usePayerLabels: true
      });
    },
    approvedPayerMetricRows() {
      return this.buildPayerMetricRows(this.data.approvedPayerStats, {
        adtpLabel: 'Weighted ADTP (from due date)',
        pcrRecentAsterisk: Boolean(this.data.approvedPayerPcrFootnote),
        usePayerLabels: true
      });
    },
    primePayerAgingTableColumns() {
      return PRIME_PAYER_AGING_TABLE_COLUMNS;
    },
    primePayerAgingPercentColumns() {
      return PRIME_PAYER_AGING_TABLE_COLUMNS.slice(1);
    },
    approvedPayerOutstandingTableColumns() {
      return APPROVED_PAYER_OUTSTANDING_TABLE_COLUMNS;
    },
    approvedPayerSalesTableColumns() {
      return APPROVED_PAYER_SALES_TABLE_COLUMNS;
    },
    approvedPayerAgingTableColumns() {
      return APPROVED_PAYER_AGING_TABLE_COLUMNS;
    },
    approvedPayerAgingPercentColumns() {
      return APPROVED_PAYER_AGING_TABLE_COLUMNS.slice(1);
    }
  },
  methods: {
    pageVisible(pageNumber) {
      if (this.downloadingPdf || !this.singlePageView) {
        return true;
      }

      return this.visiblePage === pageNumber;
    },
    async downloadPdf() {
      if (this.downloadingPdf || !this.$refs.pagesContainer) return;

      this.downloadingPdf = true;

      try {
        await this.$nextTick();
        const pageElements = this.$refs.pagesContainer.querySelectorAll('.report-page');

        if (!pageElements.length) {
          throw new Error('No report pages found.');
        }

        const pdf = new jsPDF('p', 'in', 'letter');

        for (let index = 0; index < pageElements.length; index += 1) {
          const pageElement = pageElements[index];
          await this.waitForImages(pageElement);

          const canvas = await html2canvas(pageElement, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: PAGE_WIDTH_PX,
            height: PAGE_HEIGHT_PX,
            windowWidth: PAGE_WIDTH_PX,
            scrollX: 0,
            scrollY: 0,
            logging: false,
            onclone: (documentClone) => {
              this.preparePageClone(documentClone, index);
            }
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.98);
          const renderWidth = PDF_WIDTH_IN;
          const renderHeight = (canvas.height / canvas.width) * renderWidth;

          if (index > 0) {
            pdf.addPage('letter', 'p');
          }

          if (renderHeight > PDF_HEIGHT_IN) {
            pdf.addImage(imgData, 'JPEG', 0, 0, renderWidth, PDF_HEIGHT_IN);
          } else {
            pdf.addImage(imgData, 'JPEG', 0, 0, renderWidth, renderHeight);
          }
        }

        pdf.save(this.pdfFilename);
        this.$bvToast.toast('Payer assessment report downloaded successfully.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating payer report PDF:', error);
        this.$bvToast.toast(
          'Unable to generate PDF. Please try again.',
          {
            title: 'Download failed',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.downloadingPdf = false;
      }
    },
    preparePageClone(documentClone, pageIndex) {
      const previewRoot = documentClone.querySelector('.payer-score-report-preview');
      if (previewRoot) {
        previewRoot.classList.add('is-exporting');
      }

      const pages = documentClone.querySelectorAll('.report-page');
      const clonedPage = pages[pageIndex];

      if (!clonedPage) return;

      clonedPage.style.width = `${PAGE_WIDTH_PX}px`;
      clonedPage.style.minHeight = `${PAGE_HEIGHT_PX}px`;
      clonedPage.style.height = `${PAGE_HEIGHT_PX}px`;
      clonedPage.style.boxShadow = 'none';
      clonedPage.style.border = 'none';
      clonedPage.style.margin = '0';
      clonedPage.style.overflow = 'hidden';
    },
    waitForImages(container) {
      const images = Array.from(container.querySelectorAll('img'));

      return Promise.all(images.map((image) => {
        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      }));
    },
    displayValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      return String(value);
    },
    formatRichText(value) {
      if (!value) return '—';
      return String(value)
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br />');
    },
    formatTableAmount(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).replace(/,/g, '').trim();
      const number = Number(normalized);
      if (Number.isNaN(number)) return String(value);
      return Math.round(number).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    formatPercentValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).replace(/%/g, '').trim();
      const number = Number(normalized);
      if (Number.isNaN(number)) return String(value);
      const formatted = Number.isInteger(number) ? String(number) : number.toFixed(1);
      return `${formatted}%`;
    },
    formatAdtpValue(value) {
      if (value === undefined || value === null || value === '') return '—';
      const number = Number(value);
      if (Number.isNaN(number)) return String(value);
      if (number === 0) return '0';
      if (Number.isInteger(number)) return String(number);
      return number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    },
    buildPayerMetricRows(stats, options = {}) {
      const values = stats || {};
      const adtpLabel = options.adtpLabel || 'Weighted ADTP (from due date)';
      const pcrRecentAsterisk = Boolean(options.pcrRecentAsterisk);
      const pcrLabel = options.usePayerLabels
        ? 'Average Payment Completion'
        : 'Average Payment Completion Ratio';
      const sixDprLabel = options.usePayerLabels
        ? 'Average <=60 Days Payment Completion'
        : 'Average <=60 Days Payment Completion Ratio';

      return [
        {
          label: 'Average Length of Relationship',
          recent: this.formatLorDisplay(values.lor_recent),
          lifetime: this.formatLorDisplay(values.lor_lifetime)
        },
        {
          label: pcrLabel,
          recent: this.formatPercentDisplay(values.pcr_recent, pcrRecentAsterisk),
          lifetime: this.formatPercentDisplay(values.pcr_lifetime)
        },
        {
          label: sixDprLabel,
          recent: this.formatPercentDisplay(values.six_dpr_recent),
          lifetime: this.formatPercentDisplay(values.six_dpr_lifetime)
        },
        {
          label: adtpLabel,
          recent: this.formatDaysDisplay(values.adtp_recent),
          lifetime: this.formatDaysDisplay(values.adtp_lifetime)
        }
      ];
    },
    formatLorDisplay(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      if (normalized.toUpperCase() === 'NA') return 'NA';
      if (/months?$/i.test(normalized)) return normalized;
      return `${normalized} Months`;
    },
    formatPercentDisplay(value, withAsterisk = false) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      const formatted = normalized.includes('%') ? normalized : `${normalized}%`;
      return withAsterisk ? `${formatted}*` : formatted;
    },
    formatDaysDisplay(value) {
      if (value === undefined || value === null || value === '') return '—';
      const normalized = String(value).trim();
      if (!normalized || normalized === '—') return '—';
      if (/days?$/i.test(normalized)) return normalized;
      return `${normalized} Days`;
    }
  }
};
</script>

<style lang="scss" scoped>
.payer-score-report-preview {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;

  &.is-embedded {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  &.is-single-page-view {
    .pdf-export-content {
      display: flex;
      justify-content: center;
    }

    .preview-header {
      margin-bottom: 1rem;
    }
  }
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.preview-section-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
}

.preview-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.preview-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.preview-action-btn-primary {
  border: none;
  background: #4f81bd;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #3d6a9e;
  }
}

.pdf-export-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.is-exporting .pdf-export-content {
  gap: 0;
}

.report-page {
  position: relative;
  width: 816px;
  min-height: 1056px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #d1d5db;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  padding: 28px 34px 56px;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
  color: #111827;
}

.is-exporting .report-page {
  box-shadow: none;
  border: none;
}

.report-page-cover {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.report-page-mini-header {
  font-size: 11px;
  color: #6b7280;
}

.report-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.report-page-header-label {
  font-size: 11px;
  color: #6b7280;
}

.logo-img {
  height: 22px;
}

.report-banner,
.report-section-banner {
  background: #2874E9;
  color: #ffffff;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 8px 12px;
  margin-bottom: 14px;
  font-size: 15px;
}

.report-banner-left {
  text-align: left;
}

.report-section-banner {
  margin-top: 18px;
}

.report-info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-top: 1px solid #111827;
  border-left: 1px solid #111827;
  table-layout: fixed;
  font-size: 12px;
  margin-bottom: 14px;

  th,
  td {
    border: none;
    border-right: 1px solid #111827;
    border-bottom: 1px solid #111827;
    padding: 6px 8px;
    vertical-align: top;
  }

  th {
    background: #f3f4f6;
    font-weight: 700;
    text-align: left;
  }
}

.report-info-table:not(.report-requirement-table):not(.report-summary-panel-table) {
  tbody td:nth-child(odd) {
    width: 22%;
  }
}

.label-cell {
  font-weight: 700;
}

.report-subheading {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
}

.report-paragraph {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.45;
  text-align: justify;
}

.report-summary-panel {
  border: 1px solid #111827;
  padding: 0;
  margin-bottom: 14px;
  overflow: hidden;

  .report-subheading,
  .report-paragraph {
    padding: 12px 14px 0;
  }

  .report-summary-panel-table {
    width: 100%;
    margin: 0;
    border: none;
    border-top: 1px solid #111827;
    border-left: none;
    border-collapse: collapse;
    table-layout: fixed;
    margin-bottom: 0;
    max-width: none;

    td {
      border: none;
      border-bottom: 1px solid #111827;
      padding: 7px 10px;
      vertical-align: middle;
      font-weight: 400;
    }

    td:first-child {
      width: 58%;
      border-right: 1px solid #111827;
    }

    td.label-cell {
      font-weight: 700;
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
}

.report-requirement-panel {
  .report-paragraph {
    margin-bottom: 12px;
  }
}

.payer-metrics-section {
  margin: 0;
}

.payer-metrics-section-spaced {
  margin-top: 16px;
}

.payer-metrics-text-box {
  border: 1px solid #111827;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.payer-metrics-overview-list {
  margin: 0;
}

.payer-metrics-overview-heading {
  margin: 0 0 8px;
}

.payer-metrics-footnote {
  margin: 8px 0 0;
  font-size: 10px;
  font-style: italic;
  line-height: 1.4;
  color: #374151;
}

.payer-metrics-table {
  margin-bottom: 0;

  thead th {
    background: #4b5563;
    color: #ffffff;
  }

  tbody td:nth-child(odd) {
    width: 46%;
  }

  tbody td:nth-child(even),
  tbody td:nth-child(3) {
    width: 27%;
  }
}

.industry-metrics-table {
  tbody td:first-child {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: top;
  }
}

.report-footnotes {
  margin-top: 18px;
  font-size: 10px;
  color: #374151;
  line-height: 1.4;

  p {
    margin: 0 0 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.conclusion-list {
  margin: 0;
  padding-left: 22px;
  font-size: 11px;
  line-height: 1.5;
  color: #111827;

  li {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.conclusion-empty {
  list-style: none;
  margin-left: -16px;
}

.report-page-conclusion {
  .report-banner {
    margin-top: 42px;
    margin-bottom: 28px;
  }
}

.payer-conclusion-panel {
  min-height: 500px;
  border: 1px solid #111827;
  padding: 6px 10px 16px;
}

.payer-conclusion-heading {
  margin: 0 0 32px;
  font-size: 16px;
  font-weight: 700;
}

.payer-recommendations-heading {
  margin-top: 36px;
  margin-bottom: 8px;
  font-size: 13px;
}

.payer-conclusion-list {
  padding-left: 48px;
  padding-right: 14px;
  font-size: 13px;
  line-height: 1.65;

  li {
    margin-bottom: 4px;
  }
}

.report-page-appendix {
  .report-banner {
    margin-top: 42px;
    margin-bottom: 28px;
  }
}

.payer-appendix-panel {
  min-height: 500px;
  border: 1px solid #111827;
  padding: 6px 10px 16px;
}

.payer-appendix-heading {
  margin: 0 0 32px;
  font-size: 16px;
  font-weight: 700;
}

.appendix-list {
  margin: 0;
  padding-left: 48px;
  padding-right: 14px;
  font-size: 13px;
  line-height: 1.65;
  color: #111827;

  li {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.prime-payer-outstanding-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    white-space: normal;
    word-break: break-word;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: top;
  }

  thead th:nth-child(1),
  tbody td:nth-child(1) {
    width: 37%;
  }

  thead th:nth-child(2),
  tbody td:nth-child(2) {
    width: 24%;
  }

  thead th:nth-child(3),
  tbody td:nth-child(3) {
    width: 12%;
  }

  thead th:nth-child(4),
  tbody td:nth-child(4) {
    width: 10%;
  }

  thead th:nth-child(5),
  tbody td:nth-child(5) {
    width: 10%;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    text-align: right;
    white-space: nowrap;
  }
}

.approved-payer-outstanding-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    text-align: center;
    white-space: normal;
    word-break: break-word;
  }

  thead th.payer-name-header {
    text-align: left;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: middle;
    text-align: center;
  }

  tbody td:first-child {
    text-align: left;
  }

  tbody td.sector-cell {
    text-align: left;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    text-align: center;
    white-space: nowrap;
  }
}

.prime-payer-sales-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    white-space: normal;
    word-break: break-word;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: top;
  }

  thead th:nth-child(1),
  tbody td:nth-child(1) {
    width: 30%;
  }

  thead th:nth-child(2),
  tbody td:nth-child(2) {
    width: 22%;
  }

  thead th:nth-child(3),
  tbody td:nth-child(3) {
    width: 12%;
  }

  thead th:nth-child(4),
  tbody td:nth-child(4) {
    width: 10%;
  }

  thead th:nth-child(5),
  tbody td:nth-child(5) {
    width: 26%;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    text-align: right;
    white-space: nowrap;
  }
}

.approved-payer-sales-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    text-align: center;
    white-space: normal;
    word-break: break-word;
  }

  thead th.payer-name-header {
    text-align: left;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: middle;
    text-align: center;
  }

  tbody td:first-child {
    text-align: left;
  }

  tbody td.sector-cell,
  tbody td.parent-cell {
    text-align: left;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    text-align: center;
    white-space: nowrap;
  }
}

.prime-payer-aging-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    text-align: center;
    white-space: normal;
    word-break: break-word;
  }

  thead th.payer-name-header {
    text-align: left;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: middle;
    text-align: center;
  }

  tbody td:first-child {
    text-align: left;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    white-space: nowrap;
  }
}

.approved-payer-aging-table {
  margin-bottom: 0;
  table-layout: fixed;

  thead th {
    background: #4b5563;
    color: #ffffff;
    font-weight: 700;
    font-size: 11px;
    line-height: 1.3;
    vertical-align: middle;
    text-align: center;
    white-space: normal;
    word-break: break-word;
  }

  thead th.payer-name-header {
    text-align: left;
  }

  tbody td {
    font-size: 11px;
    line-height: 1.35;
    vertical-align: middle;
    text-align: center;
  }

  tbody td:first-child {
    text-align: left;
  }

  tbody tr.row-alt td {
    background: #f9fafb;
  }

  .numeric-cell {
    white-space: nowrap;
  }
}

.cover-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 120px 0;
}

.cover-title {
  margin: 0 0 28px;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
}

.cover-subtitle {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.cover-merchant {
  display: inline-block;
}

.cover-separator {
  margin: 24px 0 0;
  font-size: 24px;
  font-weight: 400;
  color: #111827;
  line-height: 1;
}

.report-page-footer {
  position: absolute;
  left: 34px;
  right: 34px;
  bottom: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 2px solid #7f1d1d;
  font-size: 11px;
  color: #6b7280;
}

.report-page-placeholder {
  display: flex;
  flex-direction: column;
}

.placeholder-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 800px;
}

.placeholder-label {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
}
</style>
